import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { plantsAPI, externalAPI } from '../services/api';
import ThemeToggle from '../components/ThemeToggle';
import ReportBugButton from '../components/ReportBugButton';
import './AddEditPlant.css';

function AddEditPlant() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    name: '',
    species: '',
    acquisitionDate: '',
    imageUrl: '',
    careNotes: '',
    soilMixNotes: '',
    category: 'Other',
    tags: [],
    favorite: false,
    wishlist: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [loadingExternalData, setLoadingExternalData] = useState(false);
  const searchTimeoutRef = useRef(null);
  const suggestionsRef = useRef(null);

  useEffect(() => {
    if (isEdit) {
      fetchPlant();
    } else {
      // Check if we have identified plant data from navigation state
      if (location.state?.identifiedPlant) {
        const plant = location.state.identifiedPlant;
        setFormData(prev => ({
          ...prev,
          name: plant.name || '',
          species: plant.species || '',
          imageUrl: plant.imageUrl || '',
          category: plant.category || 'Other',
          wishlist: plant.wishlist || false,
          careNotes: plant.careNotes || '',
          soilMixNotes: plant.soilMixNotes || ''
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, location.state]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchPlant = async () => {
    try {
      const response = await plantsAPI.getOne(id);
      const plant = response.data;
      setFormData({
        name: plant.name,
        species: plant.species || '',
        acquisitionDate: plant.acquisitionDate 
          ? new Date(plant.acquisitionDate).toISOString().split('T')[0] 
          : '',
        imageUrl: plant.imageUrl || '',
        careNotes: plant.careNotes || '',
        soilMixNotes: plant.soilMixNotes || '',
        category: plant.category,
        tags: plant.tags || [],
        favorite: plant.favorite,
        wishlist: plant.wishlist || false
      });
    } catch (err) {
      setError('Failed to load plant details.');
      console.error(err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });

    // Trigger plant search when name field changes
    if (name === 'name') {
      handlePlantNameChange(value);
    }
  };

  const handlePlantNameChange = (value) => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Don't search if less than 2 characters
    if (value.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Debounce search - wait 500ms after user stops typing
    searchTimeoutRef.current = setTimeout(async () => {
      setLoadingSuggestions(true);
      try {
        const response = await externalAPI.searchPlants(value);
        setSuggestions(response.data.plants || []);
        setShowSuggestions(true);
      } catch (err) {
        console.error('Failed to search plants:', err);
        setSuggestions([]);
      } finally {
        setLoadingSuggestions(false);
      }
    }, 500);
  };

  const handleSuggestionClick = async (plant) => {
    // Update the name field
    setFormData(prev => ({
      ...prev,
      name: plant.name,
      species: plant.family || prev.species,
      imageUrl: plant.imageUrl || prev.imageUrl
    }));
    
    setShowSuggestions(false);
    setSuggestions([]);

    // Fetch additional care tips and soil mix information
    if (plant.name) {
      setLoadingExternalData(true);
      try {
        const [careTipsRes, soilMixRes] = await Promise.all([
          externalAPI.searchCareTips(plant.name).catch(err => ({ data: { careTips: [] } })),
          externalAPI.searchSoilMix(plant.name).catch(err => ({ data: { soilMixInfo: [] } }))
        ]);

        const careTips = careTipsRes.data.careTips || [];
        const soilMixInfo = soilMixRes.data.soilMixInfo || [];

        // Format care tips
        const careText = careTips.length > 0
          ? careTips.map(tip => tip.snippet).filter(Boolean).join('\n\n')
          : '';

        // Format soil mix info
        const soilText = soilMixInfo.length > 0
          ? soilMixInfo.map(info => info.snippet).filter(Boolean).join('\n\n')
          : '';

        setFormData(prev => ({
          ...prev,
          careNotes: careText || prev.careNotes,
          soilMixNotes: soilText || prev.soilMixNotes
        }));
      } catch (err) {
        console.error('Failed to fetch external data:', err);
      } finally {
        setLoadingExternalData(false);
      }
    }
  };

  const handleAddTag = (e) => {
    e.preventDefault();
    const trimmedTag = tagInput.trim();
    if (trimmedTag && !formData.tags.includes(trimmedTag)) {
      setFormData({
        ...formData,
        tags: [...formData.tags, trimmedTag]
      });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag(e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        acquisitionDate: formData.acquisitionDate || null
      };

      if (isEdit) {
        await plantsAPI.update(id, submitData);
      } else {
        await plantsAPI.create(submitData);
      }
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save plant. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-edit-plant">
      <ReportBugButton />
      <header className="form-header">
        <div className="form-header-left">
          <button onClick={() => navigate(-1)} className="btn-back">
            ← Back
          </button>
          <h1>{isEdit ? 'Edit Plant' : 'Add New Plant'}</h1>
        </div>
        <ThemeToggle />
      </header>

      <div className="form-container">
        {error && <div className="error-message">{error}</div>}
        {loadingExternalData && (
          <div className="info-message">
            🌱 Loading care tips and soil mix information...
          </div>
        )}

        <form onSubmit={handleSubmit} className="plant-form">
          <div className="form-group" ref={suggestionsRef}>
            <label htmlFor="name">Plant Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="e.g., Monstera Deliciosa"
              autoComplete="off"
            />
            {loadingSuggestions && (
              <div className="suggestions-loading">Searching plants...</div>
            )}
            {showSuggestions && suggestions.length > 0 && (
              <div className="suggestions-dropdown">
                {suggestions.map((plant) => (
                  <div
                    key={plant.id}
                    className="suggestion-item"
                    onClick={() => handleSuggestionClick(plant)}
                  >
                    <div className="suggestion-content">
                      {plant.imageUrl && (
                        <img 
                          src={plant.imageUrl} 
                          alt={plant.name}
                          className="suggestion-image"
                        />
                      )}
                      <div className="suggestion-text">
                        <div className="suggestion-name">{plant.name}</div>
                        {plant.scientificName && (
                          <div className="suggestion-scientific">
                            {plant.scientificName}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {showSuggestions && suggestions.length === 0 && !loadingSuggestions && (
              <div className="suggestions-empty">No plants found</div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="species">Species</label>
            <input
              type="text"
              id="species"
              name="species"
              value={formData.species}
              onChange={handleChange}
              placeholder="e.g., Araceae"
            />
          </div>

          <div className="form-group">
            <label htmlFor="category">Category</label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleChange}
            >
              <option value="Succulent">Succulent</option>
              <option value="Houseplant">Houseplant</option>
              <option value="Herb">Herb</option>
              <option value="Outdoor">Outdoor</option>
              <option value="Flower">Flower</option>
              <option value="Tree">Tree</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="tags">Tags</label>
            <div className="tags-input-container">
              <div className="tags-list">
                {formData.tags.map((tag, index) => (
                  <span key={index} className="tag-badge">
                    {tag}
                    <button
                      type="button"
                      className="tag-remove"
                      onClick={() => handleRemoveTag(tag)}
                      aria-label={`Remove ${tag} tag`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div className="tag-input-wrapper">
                <input
                  type="text"
                  id="tags"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagInputKeyDown}
                  placeholder="Add a tag and press Enter"
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="btn-add-tag"
                  disabled={!tagInput.trim()}
                >
                  Add
                </button>
              </div>
            </div>
            <small>Add tags to help organize and search your plants (e.g., "Indoor", "Low Light", "Pet-Safe")</small>
          </div>

          <div className="form-group">
            <label htmlFor="acquisitionDate">Acquisition Date</label>
            <input
              type="date"
              id="acquisitionDate"
              name="acquisitionDate"
              value={formData.acquisitionDate}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="imageUrl">Image URL</label>
            <input
              type="url"
              id="imageUrl"
              name="imageUrl"
              value={formData.imageUrl}
              onChange={handleChange}
              placeholder="https://example.com/plant-image.jpg"
            />
            <small>Paste a URL to an image of your plant</small>
          </div>

          <div className="form-group">
            <label htmlFor="careNotes">Care Tips</label>
            <textarea
              id="careNotes"
              name="careNotes"
              value={formData.careNotes}
              onChange={handleChange}
              rows="4"
              placeholder="Water weekly, indirect sunlight, etc."
            />
          </div>

          <div className="form-group">
            <label htmlFor="soilMixNotes">Soil Mix Notes</label>
            <textarea
              id="soilMixNotes"
              name="soilMixNotes"
              value={formData.soilMixNotes}
              onChange={handleChange}
              rows="4"
              placeholder="50% potting soil, 30% perlite, 20% peat moss"
            />
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="favorite"
                checked={formData.favorite}
                onChange={handleChange}
              />
              <span>Mark as favorite</span>
            </label>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="wishlist"
                checked={formData.wishlist}
                onChange={handleChange}
              />
              <span>Add to wishlist</span>
            </label>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : (isEdit ? 'Update Plant' : 'Add Plant')}
            </button>
            <button 
              type="button" 
              onClick={() => navigate(-1)} 
              className="btn btn-secondary"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddEditPlant;
